import { IMonthlyPulseVariables } from './MonthlyPulseVariables';

export const MONTHLY_PULSE_RENDER_BASE_URL = '/members/components/monthly-pulse/render/';

const URL_WITH_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

const isAbsoluteOrFragment = (value: string): boolean =>
  value.startsWith('/') || value.startsWith('#') || URL_WITH_SCHEME.test(value);

const toAbsoluteRenderUrl = (value: string): string => {
  if (value === '' || isAbsoluteOrFragment(value)) {
    return value;
  }
  return `${MONTHLY_PULSE_RENDER_BASE_URL}${value}`;
};

/**
 * Injects the flat Monthly Pulse variables into a parsed dashboard.html document.
 *
 * Every variable key maps to an element id: <img> elements get `src` (an empty
 * string hides them via the dashboard's `img[src=""]` rule), all other elements
 * get `textContent`. All elements with a matching id are updated: the header
 * markup (and its ids) is repeated on each of the dashboard's three pages.
 * Ids without a variable and variables without an element are left untouched.
 *
 * Leaderboard rows (data-leaderboard-item) then get their icon element chosen by
 * priority: primaryColour hexagon, else imageUrl image, else rank text (hidden
 * when the rank is 0, i.e. no item at that index).
 *
 * Scripts are stripped, and relative asset urls (img src, link href) are rewritten
 * to the served render directory so they resolve inside a srcDoc iframe. A global
 * <base> is intentionally not used: it would also re-target in-document fragment
 * refs such as the leaderboard's <use href="#hexagon"> bullets.
 */
export const injectMonthlyPulseVariables = (
  doc: Document,
  variables: IMonthlyPulseVariables,
): void => {
  doc.querySelectorAll('script').forEach((script) => script.remove());

  Object.entries(variables).forEach(([key, value]) => {
    doc.querySelectorAll(`[id="${key}"]`).forEach((el) => {
      if (el.tagName === 'IMG') {
        (el as HTMLImageElement).src = String(value);
      } else {
        el.textContent = String(value);
      }
    });
  });

  applyLeaderboardItemIcons(doc, variables);
  applyDonutSegments(doc, variables);
  applyFeaturedChallengeCard(doc, variables);
  applyBadgesHeaderVisibility(doc, variables);

  doc.querySelectorAll('img[src], link[href]').forEach((el) => {
    const attribute = el.tagName === 'LINK' ? 'href' : 'src';
    const value = el.getAttribute(attribute);
    if (value !== null) {
      el.setAttribute(attribute, toAbsoluteRenderUrl(value));
    }
  });
};

const setDisplayed = (el: HTMLElement | null, displayed: boolean): void => {
  if (el) {
    el.style.display = displayed ? '' : 'none';
  }
};

// SVG donut uses pathLength=100 so percentages map directly to dash units.
// Segments are adjacent; colour shades alone separate workout types.
const DONUT_SEGMENT_GAP = 0;
// Circle stroke starts at 3 o'clock; offset 25 rotates the first segment to 12 o'clock.
const DONUT_START_OFFSET = 25;

const applyDonutSegments = (doc: Document, variables: IMonthlyPulseVariables): void => {
  const strength = Number(variables.donutStrengthPct) || 0;
  const cardio = Number(variables.donutCardioPct) || 0;
  const mobility = Number(variables.donutMobilityPct) || 0;
  const total = strength + cardio + mobility;
  const emptyEl = doc.querySelector('[data-donut-empty]') as SVGElement | null;
  if (emptyEl) {
    // Grey full ring when all workout-type counts are zero.
    // Must set an explicit display value — clearing inline style leaves CSS
    // `display: none` in place and the empty state never becomes visible.
    emptyEl.style.display = total <= 0 ? 'inline' : 'none';
  }
  const segments: Array<{ key: string; pct: number; offset: number }> = [
    { key: 'strength', pct: strength, offset: DONUT_START_OFFSET },
    { key: 'cardio', pct: cardio, offset: DONUT_START_OFFSET - strength },
    { key: 'mobility', pct: mobility, offset: DONUT_START_OFFSET - strength - cardio },
  ];
  for (const segment of segments) {
    const el = doc.querySelector(`[data-donut-segment="${segment.key}"]`) as SVGElement | null;
    if (!el) { continue; }
    const dash = Math.max(segment.pct - DONUT_SEGMENT_GAP, 0);
    const rest = 100 - dash;
    el.setAttribute('stroke-dasharray', `${dash} ${rest}`);
    el.setAttribute('stroke-dashoffset', String(segment.offset));
  }
};

const applyFeaturedChallengeCard = (doc: Document, variables: IMonthlyPulseVariables): void => {
  const card = doc.querySelector('[data-featured-challenge-card]') as HTMLElement | null;
  if (!card) { return; }
  setDisplayed(card, variables.featuredChallengeJoinVisible === 'true');
};

const applyBadgesHeaderVisibility = (doc: Document, variables: IMonthlyPulseVariables): void => {
  const header = doc.querySelector('[data-badge-subsection-header]') as HTMLElement | null;
  if (!header) { return; }
  const hasBadges = Boolean(
    variables.badge1UserName
    || variables.badge2UserName
    || variables.badge3UserName
    || variables.badge4UserName,
  );
  setDisplayed(header, hasBadges);
};

const applyLeaderboardItemIcons = (doc: Document, variables: IMonthlyPulseVariables): void => {
  doc.querySelectorAll('[data-leaderboard-item]').forEach((row) => {
    const id = row.getAttribute('data-leaderboard-item');
    if (!id) { return; }
    const colour = variables[`${id}PrimaryColour` as keyof IMonthlyPulseVariables];
    const imageUrl = variables[`${id}ImageUrl` as keyof IMonthlyPulseVariables];
    const rankValue = variables[`${id}Rank` as keyof IMonthlyPulseVariables];

    const hexagon = row.querySelector('.leaderboard-hexagon') as HTMLElement;
    const image = row.querySelector('.leaderboard-avatar') as HTMLElement;
    const rank = row.querySelector('.leaderboard-rank') as HTMLElement;

    if (colour) {
      const use = hexagon?.querySelector('use');
      if (use) { (use as SVGElement).style.fill = String(colour); }
      setDisplayed(hexagon, true);
      setDisplayed(image, false);
      setDisplayed(rank, false);
    } else if (imageUrl) {
      setDisplayed(hexagon, false);
      setDisplayed(image, true);
      setDisplayed(rank, false);
    } else {
      setDisplayed(hexagon, false);
      setDisplayed(image, false);
      setDisplayed(rank, Number(rankValue) > 0);
    }
  });
};
